import React from "react";
import { shallowEqual } from "react-redux";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import * as Styles from "./styles/Summary";
import * as Flex from "../../../../../../common-deprecated/styles/v2/toyota/globals/Flex";
import { calculateWinterWheelPrice, getCarDescription } from "../../../utils/typedUtils";
import { removeServiceProduct, removeWinterWheel } from "../../../redux/actions/BasketActions";
import { BnbLabel, getBnbLabel } from "../../../utils/labels";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import SummaryRow from "./SummaryRow";
import SummaryRowWheel from "./SummaryRowWheel";
import useSummaryPack from "../../../hooks/shared/packs/useSummaryPack";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import { IconWrapper } from "../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import * as Button from "../../../../../../common-deprecated/styles/v2/toyota/components/Button";
import * as Type from "../../../../../../common-deprecated/styles/v2/toyota/globals/Type";
import SummaryCtas from "./SummaryCtas";
import BasketConfigCode from "../common/basket/summary/BasketConfigCode";
import SummaryTotalPanel from "./SummaryTotalPanel";
import { isIsrael, isRTL } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { isPassiveSummary } from "../../../utils/settings/bnbSettingUtils";
import Pack from "../step-customize/main-content/Pack";
import FinancePack from "../common/FinancePack";
import { getSelectedFinancePacksSelector } from "../../../utils/selectorUtils";
import { find } from "../../../../../../common-deprecated/utils";
import { trackChangeCustomisation, trackCustomizeConfigItem } from "../../../utils/tracking";
import useStepUrl from "../../../hooks/shared/actions/useStepUrl";
import { useDispatch } from "../../../../../../shared-logic/redux/store";
import type { FinancePackConfigurablesType } from "../../../../../shared-logic/types/FinanceTypes";

const SummaryDetails = (): JSX.Element => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const car = useBnbSelector((state) => state.car);
    const basket = useBnbSelector((state) => state.basket);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const financePacks = useBnbSelector(getSelectedFinancePacksSelector, shallowEqual);
    const financePacksServiceProducts = useBnbSelector((state) =>
        state.items.financePacks.reduce((packServiceProducts, pack) => {
            return [...packServiceProducts, ...pack.serviceProducts];
        }, [] as FinancePackConfigurablesType[]),
    );

    const commonSettings = useBnbSelector((state) => state.commonSettings);

    // Hides change grade and change customization buttons and all possibilities to remove items
    const passiveSummary = isPassiveSummary(commonSettings);

    const customizeStep = useStepUrl(NavStep.Customize, undefined, undefined, trackChangeCustomisation);

    const { packs } = useSummaryPack();
    const { colour, wheel, winterWheel, upholstery, serviceProducts } = basket;

    const filteredServiceProducts = serviceProducts.filter(
        (serviceProduct) => !find(serviceProduct.id, financePacksServiceProducts),
    );

    return (
        <>
            <Styles.DetailsWrapper id="or-build-summary-details">
                <SummaryCtas />
                {!bnbSettings.hideTyCode && <BasketConfigCode />}
                <SummaryTotalPanel />
            </Styles.DetailsWrapper>

            <Flex.Col justifyContent="space-between" display="flex" alignItems="center" p={0} mt={5}>
                <Styles.Title isRTL={isRTL(commonSettings)}>
                    <Type.H3 m={0}>
                        <BnbLabel label="basketYourModel" params={{ name: car.model.webtitleSectionName }} />
                    </Type.H3>

                    {!passiveSummary && (
                        <Button.Link as="a" hasIcon href={customizeStep.href} onClick={customizeStep.onClick}>
                            <IconWrapper>
                                <Icon variant={isRTL(commonSettings) ? "arrowRight" : "arrowLeft"} />
                            </IconWrapper>
                            <BnbLabel label="changeCustomisation" />
                        </Button.Link>
                    )}
                </Styles.Title>
            </Flex.Col>

            <SummaryRow
                text={getCarDescription(car, true)}
                price={!isIsrael(commonSettings.culture.name) ? car.price : undefined}
            />

            <Flex.Col justifyContent="space-between" display="flex" alignItems="center" p={0} mt={5}>
                <Styles.Title>
                    <Type.H3 m={0}>
                        <BnbLabel label="yourCustomisation" params={{ name: car.model.webtitleSectionName }} />
                    </Type.H3>
                </Styles.Title>
            </Flex.Col>

            <SummaryRow text={colour.name} price={colour.price} itemId={colour.id} />
            <SummaryRowWheel text={wheel.name} price={wheel.price} />
            {winterWheel && (
                <SummaryRow
                    text={winterWheel.name}
                    popoverContent={
                        winterWheel.installTheWheels &&
                        !winterWheel.installationCost &&
                        getBnbLabel({ bnbSettings, commonSettings }, "winterWheelsModalExcludingInstallationCosts")
                    }
                    price={calculateWinterWheelPrice(winterWheel)}
                    itemId={winterWheel.id}
                    onRemove={
                        passiveSummary
                            ? undefined
                            : () => {
                                  trackCustomizeConfigItem(NavStep.Summary, "remove-winter-set");
                                  dispatch(removeWinterWheel());
                              }
                    }
                />
            )}
            <SummaryRow text={upholstery.name} price={upholstery.price} itemId={upholstery.id} />

            {/* Packs and equipment */}
            {packs.length ? (
                <>
                    <Styles.Title pb={2} pt={3}>
                        <Type.Strong m={0}>
                            <BnbLabel label="optionalPacks" />
                        </Type.Strong>
                    </Styles.Title>
                    {packs.map((pack) => (
                        <Pack pack={pack} showImage packIndex={1} />
                    ))}
                </>
            ) : null}

            {/* active ServicePlans */}
            {(!!filteredServiceProducts.length || !!financePacks.length) && (
                <>
                    <Styles.Title>
                        <Type.Strong mb={0}>
                            <BnbLabel label="yourServicePlans" />
                        </Type.Strong>
                    </Styles.Title>
                    {filteredServiceProducts.map((serviceProduct) => (
                        <SummaryRow
                            key={serviceProduct.id}
                            text={serviceProduct.name}
                            price={serviceProduct.price}
                            itemId={serviceProduct.id}
                            financeable={serviceProduct.financeable}
                            exclusiveOnline={serviceProduct.exclusiveOnline}
                            onRemove={() => {
                                trackCustomizeConfigItem(NavStep.Summary, "remove-services");
                                dispatch(removeServiceProduct(serviceProduct));
                            }}
                        />
                    ))}
                    {financePacks.map((financePack) => (
                        <FinancePack financePack={financePack} onlyShowSelected />
                    ))}
                </>
            )}
        </>
    );
};

export default SummaryDetails;
